import { useId, useState, type ReactNode } from 'react';
import { GLOSSARY, type GlossaryKey } from '../lib/glossary.ts';

interface TermProps {
  /** Which definition to attach — see `lib/glossary.ts`. */
  term: GlossaryKey;
  /** The word as it reads in this sentence ("blanking", "blanks", "Bench
   * Boost"). The glossary holds one definition per concept; the surrounding
   * prose decides how the word is inflected. */
  children: ReactNode;
}

/**
 * A term a first-season manager might not know, with its definition one
 * click or keypress away (T-120).
 *
 * Deliberately *not* a `title=` attribute, which is the obvious shortcut and
 * fails three ways: invisible on touch, unreliably announced by screen
 * readers, and not reachable by keyboard at all. This is a real button, so it
 * is in the tab order and works the same by mouse, touch and keyboard.
 *
 * A disclosure, not an always-present description. Keeping the definition
 * permanently in the DOM was tried first and is worse for the reader it was
 * meant to help: a screen reader then recites the whole sentence every time
 * it meets the word, however many times that is on a page. `aria-expanded`
 * announces that there is something to open, and opening it is the same
 * gesture for everyone — which is the point. It also keeps the definition out
 * of every ancestor's `textContent`, where it otherwise leaks into unrelated
 * assertions and into copy-and-paste.
 *
 * Not the same as `GameweekChart`'s `gameweek-chart-tooltip`: that is a
 * hover-follows-cursor readout of the data under a chart point, driven by
 * pointer position and meaningless without one. This defines a word in
 * running prose and has to work with no pointer at all.
 */
export function Term({ term, children }: TermProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const definitionId = `${id}-definition`;

  return (
    <span className="term">
      <button
        type="button"
        className="term-trigger"
        aria-expanded={open}
        aria-describedby={open ? definitionId : undefined}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) setOpen(false);
        }}
      >
        {children}
      </button>
      {open && (
        <span id={definitionId} role="note" className="term-definition">
          {GLOSSARY[term]}
        </span>
      )}
    </span>
  );
}
